import {
  Component,
  OnInit,
  OnDestroy,
  HostBinding,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";

import { combineLatest, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { E2eLocators } from "@onsip/e2e/configs/locators";

import {
  GroupAddressService,
  GroupAddress
} from "@onsip/common/services/api/resources/groupAddress/group-address.service";

export interface GroupAddressWithExtensions extends GroupAddress {
  failover: string;
  extensions: Array<string>;
}

import { UserAliasService } from "@onsip/common/services/api/resources/userAlias/user-alias.service";
import {
  AddressSummary,
  AddressSummaryService
} from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { DynamicPricingService } from "@onsip/common/services/dynamic-pricing.service";
import { BreakpointObserver } from "@angular/cdk/layout";
import { FormatSingularAddressTypePipe } from "@onsip/web/features/shared/pipes/format-singular-address-type.pipe";
import { AdminTableAbstractComponent } from "@onsip/web/features/abstractClass/admin-table-abstract.component";
import { FormatExtensionsPipe } from "@onsip/web/features/shared/pipes/format-extensions.pipe";

@Component({
  selector: "onsip-groups-page",
  templateUrl: "./groups-page.component.html",
  styleUrls: ["./groups-page.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FormatSingularAddressTypePipe, FormatExtensionsPipe],
  standalone: false
})
export class GroupsPageComponent
  extends AdminTableAbstractComponent<GroupAddressWithExtensions>
  implements OnInit, OnDestroy
{
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;
  pricingText = "";

  selectedRows: Array<GroupAddressWithExtensions["address"]["addressId"]> = [];

  protected displayedColumnsRecord = {
    XSmall: ["address.name", "actions"],
    Small: ["address.name", "extensions", "actions"],
    Medium: ["address.name", "extensions", "ordering", "actions"],
    Large: ["address.name", "extensions", "ordering", "failover", "actions"],
    XLarge: ["address.name", "extensions", "ordering", "failover", "actions"]
  };
  protected unsubscriber = new Subscription();
  E2eLocators = E2eLocators;

  constructor(
    private groupAddress: GroupAddressService,
    private userAlias: UserAliasService,
    private addressSummaryService: AddressSummaryService,
    private snackbar: SnackbarService,
    private dynamicPricing: DynamicPricingService,
    private singularAddressPipe: FormatSingularAddressTypePipe,
    private formatExtensions: FormatExtensionsPipe,
    protected breakpointObserver: BreakpointObserver,
    protected translate: TranslateService,
    protected dialog: MatDialog,
    protected cdr: ChangeDetectorRef
  ) {
    super(breakpointObserver, cdr, dialog, translate);
  }

  ngOnInit() {
    super.ngOnInit();
    this.setPricing();
    this.setDataFilter();
  }

  getFailoverTableData(defaultAddress: AddressSummary | undefined): string {
    if (defaultAddress) {
      return `${this.singularAddressPipe.transform(defaultAddress?.addressType)}: ${
        defaultAddress?.name
      }`;
    }
    return "";
  }

  trackByGroupName(index: number, item: GroupAddress): string {
    return item.address.name;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.unsubscriber.unsubscribe();
  }

  protected performDeleteRequest(group: GroupAddress): void {
    this.groupAddress.groupAddressDelete(group).then(() => {
      this.setPricing();
      this.snackbar.openSnackBar(
        this.translate.instant("ONSIP_I18N.VALUE_HAS_BEEN_DELETED", { value: "Group" }),
        "success"
      );
    });
  }

  protected getItemValueByColumn(item: GroupAddress, columnName: string): string | number {
    return columnName.split(".").reduce((o: any, p) => o && o[p], item);
  }

  protected getItemName(group: GroupAddress): string {
    return group.address.name;
  }

  protected initDataSource(): void {
    this.addressSummaryService.addressSummaryBrowse();
    this.userAlias.userAliasBrowse();
    this.groupAddress.groupAddressBrowse();

    this.unsubscriber.add(
      combineLatest({
        groups: this.groupAddress.orgState,
        aliases: this.userAlias.orgState,
        addresses: this.addressSummaryService.orgState
      })
        .pipe(
          map(({ groups, aliases, addresses }) =>
            groups.map(group => ({
              ...group,
              extensions: aliases
                .filter(alias => alias.addressId === group.address.addressId)
                .map(alias => alias.aliasUsername),
              failover: this.getFailoverTableData(
                addresses.find(address => address.addressId === group?.defaultAddress?.addressId)
              )
            }))
          )
        )
        .subscribe(groupsWithExt => {
          this.dataSource.data = groupsWithExt;
          this.cdr.markForCheck();
          this.isDataObtained$.next(true);
        })
    );
  }

  protected setDataFilter(): void {
    this.dataSource.filterPredicate = (data, filter) => {
      const filterValue = filter.trim().toLowerCase();
      // let's be more selective with our filter and only filter by values displayed in the columns
      // so group name, extensions, and ring strategy
      const groupName = data.address.name;
      const groupExtensions = this.formatExtensions.transform(data.extensions).join();
      const groupRingStrategy: string =
        data.ordering === "parallel"
          ? this.translate.instant("ONSIP_I18N.SIMULTANEOUS_RING")
          : this.translate.instant("ONSIP_I18N.HUNT_GROUP");
      const groupFailover = data.failover;
      return (
        groupName.toLowerCase().includes(filterValue) ||
        groupExtensions.toLowerCase().includes(filterValue) ||
        groupRingStrategy.toLowerCase().includes(filterValue) ||
        groupFailover.toLowerCase().includes(filterValue)
      );
    };
  }

  private setPricing(): void {
    this.dynamicPricing.getDynamicPricing("GroupAddress", false).then(result => {
      this.pricingText = result;
      this.cdr.markForCheck();
    });
  }
}
